<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useAvaliacaoStore } from '@/stores/avaliacao'
import { formatToDateTimeBR } from '@/utils/date'
import { useNotificationStore } from '@/stores/notification';

const store = useAvaliacaoStore()
const notificationStore = useNotificationStore();

// filtros e estado UI
const search = ref('')
// cnpj, responsavel, razaosocial, nomefantasia, idrevenda, idrevendedor, telefoneCliente, idpedido
const searchField = ref('cnpj')
const notes = ref('')
const dateFrom = ref('')
const dateTo = ref('')
const perPage = ref(100)
const page = ref(1)
const loadingRequestIA = ref(false)
const loadingChangeStatus = ref(false)
const loadingResolverTodas = ref(false)

const dialogOpen = ref(false)
const selectedAvaliacao = ref(null)
const dialogResolverTodas = ref(false)
// const loadingDetail = ref(false)

// debounce simples para filtros
let debounceTimer = null
function debounceFetch(delay = 400) {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
        fetchList()
    }, delay)
}

async function fetchList() {
    await store.fetchAvaliacoes({
        notes: notes.value || undefined,
        searchField: searchField.value || undefined,
        search: search.value || undefined,
        init_created_at: dateFrom.value || undefined,
        end_created_at: dateTo.value || undefined,
        per_page: perPage.value,
        page: page.value,
    })
}

function resetFilters() {
    search.value = ''
    dateFrom.value = ''
    dateTo.value = ''
    page.value = 1
    notes.value = ''
    store.resetFilters()
}

// function openDetail(item) {
//     selectedAvaliacao.value = item
//     dialogOpen.value = true
// }

function openLinkWhatsapp(name = '', phone = '') {
    const cleaned = String(phone || '').replace(/\D/g, '')
    if (!cleaned) return
    const number = cleaned.startsWith('55') ? cleaned : `55${cleaned}`
    const url = `https://api.whatsapp.com/send?phone=${number}&text=Ol%C3%A1%20${name},%20obrigado%20por%20ter%20avaliado%20a%20entregador%20no%20Aplicativo%20Pre%C3%A7o%20do%20G%C3%A1s.%20Pode%20avaliar%20agora%20na%20sua%20loja%20do%20aplicativo%20por%20favor.%20Sua%20avalia%C3%A7%C3%A3o%20%C3%A9%20muito%20importante%20para%20n%C3%B3s.%20Obrigado.`
    window.open(url, '_blank')
}

async function createResponseIA(itemId) {
    try {
        loadingRequestIA.value = true
        await store.createResponseIA(itemId)
    } finally {
        loadingRequestIA.value = false
    }
}

async function changeStatusAvaliacaoObs(itemId) {
    try {
        loadingChangeStatus.value = true
        await store.changeStatusAvaliacaoObs(itemId)
        fetchList()
        notificationStore.showNotification('Alterado com sucesso!', 'success');
    } finally {
        loadingChangeStatus.value = false
    }
}

async function resolverTodasAvaliacoes() {
    try {
        loadingResolverTodas.value = true
        const ids = avaliacoes.value
            .map((item) => item.obs_id)
            .filter((id) => typeof id === 'number' && Number.isFinite(id))
        await store.resolveAllAvaliacaoObs(ids)
        fetchList()
        notificationStore.showNotification('Todas as avaliações foram resolvidas!', 'success');
    } finally {
        dialogResolverTodas.value = false
        loadingResolverTodas.value = false
    }
}

// watchers
watch([perPage, page], () => {
    fetchList()
})

watch([search, dateFrom, dateTo], () => {
    page.value = 1
    debounceFetch()
})

onMounted(() => {
    // data inicial e fim atual
    const now = new Date()

    dateFrom.value = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString().substr(0, 10)
    dateTo.value = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString().substr(0, 10)

    fetchList()

    // listener para novas avaliações
    window.addEventListener('notifications:new-ratings', fetchList)

    // garante remoção quando a página for descarregada
    window.addEventListener('beforeunload', () => {
        window.removeEventListener('notifications:new-ratings', fetchList)
    })
})

// computed
const avaliacoes = computed(() => store.items || [])
const loading = computed(() => store.loading)
const meta = computed(() => store.meta || { current_page: 1, last_page: 1, total: 0 })
</script>

<template>
    <div>
        <v-row class="mb-3">
            <v-col cols="12" class="d-flex align-center">
                <h1 class="text-h4">Avaliações</h1>
                <v-spacer></v-spacer>
            </v-col>
        </v-row>

        <v-card class="pa-3 mb-3">
            <v-row align="center" gutter="16">
                <v-col cols="12" md="2">
                    <v-select v-model="notes" :items="[
                        { title: 'Todas', value: '' },
                        { title: '1 estrela', value: '1' },
                        { title: '2 estrelas', value: '2' },
                        { title: '3 estrelas', value: '3' },
                        { title: '4 estrelas', value: '4' },
                        { title: '5 estrelas', value: '5' },
                        { title: '2 Estrelas ou menos', value: '-2' },
                        { title: '3 Estrelas ou menos', value: '-3' },
                        { title: '4 Estrelas ou menos', value: '-4' },
                        { title: '5 Estrelas ou menos', value: '-5' }
                    ]" label="Notas" density="compact" hide-details />
                </v-col>

                <v-col cols="12" md="2">
                    <v-select v-model="searchField" :items="[
                        { title: 'ID Pedido', value: 'idpedido' },
                        { title: 'ID Revenda', value: 'idrevenda' },
                        { title: 'ID Revendedor', value: 'idrevendedor' },
                        { title: 'CNPJ', value: 'cnpj' },
                        { title: 'Responsável', value: 'responsavel' },
                        { title: 'Razão Social', value: 'razaosocial' },
                        { title: 'Nome Fantasia', value: 'nomefantasia' },
                        { title: 'Telefone Cliente', value: 'telefoneCliente' },
                    ]" label="Campo de pesquisa" density="compact" hide-details />
                </v-col>

                <v-col cols="12" md="4">
                    <v-text-field v-model="search" label="Pesquisar" prepend-inner-icon="mdi-magnify" density="compact"
                        hide-details clearable />
                </v-col>

                <v-col cols="12" md="2">
                    <v-text-field v-model="dateFrom" type="date" label="Data início" density="compact" hide-details />
                </v-col>

                <v-col cols="12" md="2">
                    <v-text-field v-model="dateTo" type="date" label="Data fim" density="compact" hide-details />
                </v-col>
            </v-row>

            <v-row align="center">
                <v-col cols="12" md="6" class="d-flex align-center ga-2">
                    <v-btn text @click="resetFilters">Limpar</v-btn>

                    <v-btn color="success" variant="tonal" @click="dialogResolverTodas = true">
                        <v-icon start icon="mdi-check-all" />
                        Resolver Todas
                    </v-btn>

                    <v-btn color="primary" @click="fetchList">
                        Aplicar
                        <i class="mdi mdi-magnify"></i>
                    </v-btn>
                </v-col>
            </v-row>
        </v-card>

        <v-alert v-if="store.error" type="error" class="mb-4">{{ store.error }}</v-alert>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2"></v-progress-linear>

        <v-data-table :headers="[
            { title: 'Id Pedido', key: 'idpedido' },
            { title: 'Cliente', key: 'cliente_nome', sortable: false },
            { title: 'Revendedor', key: 'nome', sortable: false },
            { title: 'Nota', key: 'notas' },
            { title: 'Situação', key: 'resolvido' },
            { title: 'Obs', key: 'obs', sortable: false },
            { title: 'Resposta', key: 'resposta_mensagem', sortable: false },
            { title: 'Criado em', key: 'datetime' },
            { title: 'Ações', key: 'actions', sortable: false }
        ]" :items="avaliacoes" class="custom-table" :items-per-page="perPage">
            <template #item="{ item }">
                <tr>
                    <td>{{ item.idpedido }}</td>
                    <td>
                        <div class="d-flex flex-column">
                            <p>
                                {{ item?.cliente?.nome || item?.cliente_nome || item?.nome || '-' }}
                            </p>
                            <p>
                                {{ item?.telefone1 }}
                            </p>
                        </div>

                    </td>
                    <td>
                        <div>
                            <p>#{{ item?.idrevendedor || '-' }}</p>
                            <p>

                                {{ item?.revendedor?.nome ||
                                    item?.nomefantasia || '-' }}
                            </p>
                        </div>
                    </td>
                    <td>{{ item.nota ?? item.score ?? item.notas ?? '-' }}</td>
                    <td>
                        <v-switch v-if="item.obs_id" :model-value="Boolean(Number(item.resolvido))"
                            :loading="loadingChangeStatus" inset
                            :color="Boolean(Number(item.resolvido)) ? 'success' : 'secondary'"
                            @click="changeStatusAvaliacaoObs(item.obs_id)" />
                        <span v-else>-</span>
                    </td>
                    <td>
                        <div
                            style="max-width:360px; white-space:pre-wrap; word-break:break-word; overflow-wrap:anywhere;">
                            {{ item.comentario || item.obs || '-' }}
                        </div>
                    </td>
                    <td>
                        <div
                            style="max-width:360px; white-space:pre-wrap; word-break:break-word; overflow-wrap:anywhere;">
                            {{ item.resposta_mensagem || '-' }}
                        </div>
                    </td>
                    <td>{{ item?.datetime ? formatToDateTimeBR(item.datetime) : '-' }}</td>
                    <td>
                        <v-icon class="mr-1" icon="mdi-whatsapp"
                            @click="openLinkWhatsapp(item?.nome, item?.telefone1)" />
                        <v-icon icon="mdi-message-text" @click="createResponseIA(item.obs_id)" />
                    </td>
                </tr>
            </template>

            <template #bottom>
                <div class="d-flex justify-space-between align-center px-4 py-2">
                    <div class="d-flex align-center">
                        <span class="text-caption text-secondary mr-4">
                            Total de {{ meta.total || avaliacoes.length }} item{{ (meta.total || 0) !== 1 ? 's' : '' }}
                        </span>

                        <v-select v-model="perPage" :items="[10, 25, 50, 100]" label="Itens por página"
                            variant="outlined" dense hide-details style="width: 140px" />
                    </div>

                    <v-pagination v-if="meta.last_page > 1" :model-value="page" :length="meta.last_page"
                        :total-visible="5" @update:model-value="(newPage) => page = newPage" rounded
                        class="my-2"></v-pagination>
                </div>
            </template>
        </v-data-table>

        <v-dialog v-model="loadingRequestIA" persistent width="400">
            <v-card class="pa-4 d-flex flex-column align-center">
                <v-progress-circular indeterminate color="primary" size="48" class="mb-4" />
                <div class="text-subtitle-1 mb-2">Gerando resposta com IA</div>
                <div class="text-caption text-center">
                    Aguarde enquanto o sistema cria uma mensagem automática. Isso pode levar alguns segundos.
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogResolverTodas" max-width="480">
            <v-card>
                <v-card-title class="text-h6">Resolver todas as avaliações</v-card-title>
                <v-card-text>
                    Isso irá marcar como resolvidas todas as avaliações com observação visíveis na listagem atual.
                    Tem certeza que deseja continuar?
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text :disabled="loadingResolverTodas" @click="dialogResolverTodas = false">Cancelar</v-btn>
                    <v-btn color="success" :loading="loadingResolverTodas"
                        @click="resolverTodasAvaliacoes">Confirmar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogOpen" max-width="700">
            <v-card>
                <v-card-title class="text-h6">Detalhes da Avaliação #{{ selectedAvaliacao?.id }}</v-card-title>
                <v-card-text>
                    <div><strong>Cliente:</strong> {{ selectedAvaliacao?.cliente?.nome ||
                        selectedAvaliacao?.cliente_nome || selectedAvaliacao?.nome || '-'
                    }}</div>
                    <div class="mt-2"><strong>Nota:</strong> {{ selectedAvaliacao?.notas ?? '-' }}</div>
                    <div class="mt-2"><strong>Comentário:</strong></div>
                    <div style="background:#f5f5f5;padding:10px;border-radius:4px;margin-top:6px;">
                        {{ selectedAvaliacao?.comentario || selectedAvaliacao?.comment || selectedAvaliacao?.obs || '-'
                        }}
                    </div>
                    <div class="mt-2 text-caption">Criado em: {{ selectedAvaliacao?.datetime ?
                        formatToDateTimeBR(selectedAvaliacao.datetime) : '-' }}</div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogOpen = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<style scoped>
/* estilos mínimos específicos para a lista de avaliações */
</style>
